import {
  BranchesOutlined,
  ControlOutlined,
  DesktopOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FireOutlined,
  FlagOutlined,
  FunnelPlotOutlined,
  HomeOutlined,
  PlayCircleOutlined,
  PlaySquareOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { selectHasPermitionMaster } from '@features/auth/store/auth.selectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { env } from 'src/app/env';

import { recordAccessClick } from '../utils/sideMenuUtils';

export type MenuItem = {
  key: React.Key;
  icon: JSX.Element | null;
  label: string;
  children: MenuItem[] | null;
  onClick: (() => void) | null;
};

export type BaseMenuItem = {
  label: string;
  key: string;
  icon: JSX.Element | null;
  redirect: string | null;
  children?: BaseMenuItem[];
};

export function useSideMenuItems() {
  const navigate = useNavigate();
  const hasPermissionMaster = useSelector(selectHasPermitionMaster);
  const location = useLocation();

  const tenantId = env.TENANT_ID;

  function setItem(
    label: React.ReactNode,
    key: React.Key,
    icon: React.ReactNode | undefined,
    onClickRedirect: string | null,
    children?: MenuItem[]
  ) {
    const onClick =
      onClickRedirect &&
      (() => {
        navigate(onClickRedirect);
        recordAccessClick(key);
      });

    return {
      key,
      icon,
      label,
      children,
      onClick,
    } as MenuItem;
  }

  const baseMenuItems = useMemo(() => {
    return [
      { label: 'Home', key: '1', icon: <HomeOutlined />, redirect: '/home' },
      {
        label: 'Clientes',
        key: '2',
        icon: <UserOutlined />,
        redirect: null,
        children: [
          {
            label: 'Gerenciamento',
            key: '2.1',
            icon: null,
            redirect: '/clientes',
          },
          {
            label: 'Cadastrar novo cliente',
            key: '2.2',
            icon: null,
            redirect: '/clientes/novo',
          },
        ],
      },
      {
        label: 'Permissões',
        key: '3',
        icon: <FileTextOutlined />,
        redirect: '/permissions',
      },
      {
        label: 'Segmentos',
        key: '4',
        icon: <ControlOutlined />,
        redirect: null,
        children: [
          {
            label: 'Gerenciamento',
            key: '4.1',
            icon: null,
            redirect: '/screens/segments/manage',
          },
          {
            label: 'Ordenação',
            key: '4.2',
            icon: null,
            redirect: '/screens/segments/ordering',
          },
        ],
      },
      {
        label: 'Assets',
        key: '5',
        icon: <DesktopOutlined />,
        redirect: '/screens/Assets',
      },
      {
        label: 'Trilhas',
        key: '6',
        icon: <PlayCircleOutlined />,
        redirect: '/programs/v2',
      },
      {
        label: 'Assinaturas',
        key: '8',
        icon: <FireOutlined />,
        redirect: '/assinaturas/cancelamentos/agendas',
      },
      {
        label: 'Gestão de funil',
        key: '9',
        icon: <FunnelPlotOutlined />,
        redirect: null,
        children: [
          {
            label: 'Upsell',
            key: '9.1',
            icon: null,
            redirect: '/upsell/funnel',
          },
          {
            label: 'Cancelamento',
            key: '9.2',
            icon: null,
            redirect: '/unsubscribe/funnel',
          },
        ],
      },
      {
        label: 'Financeiro e jurídico',
        key: '10',
        icon: <FileSearchOutlined />,
        redirect: null,
        children: [
          {
            label: 'Anonimização LGPD',
            key: '10.1',
            icon: null,
            redirect: '/lgpd',
          },
          {
            label: 'Agendamento Freezing',
            key: '10.2',
            icon: null,
            redirect: '/freezing',
          },
        ],
      },
      hasPermissionMaster
        ? {
            label: 'Produtos e Ofertas',
            key: '11',
            icon: <PlaySquareOutlined />,
            redirect: '/products',
          }
        : null,
      {
        label: 'Gestão de modais',
        key: '12',
        icon: <FileDoneOutlined />,
        redirect: null,
        children: [
          {
            label: 'Gerenciamento',
            key: '12.1',
            icon: null,
            redirect: '/screens/modals',
          },
          {
            label: 'Ordenação',
            key: '12.2',
            icon: null,
            redirect: '/screens/modals/ordering',
          },
        ],
      },
      {
        label: 'Gestão de cupons',
        key: '13',
        icon: <TagsOutlined />,
        redirect: '/screens/coupons',
      },
      {
        label: 'Order Bump',
        key: '14',
        icon: <FlagOutlined />,
        redirect: '/screens/order-bump',
      },
      {
        label: 'Gestão de links',
        key: '15',
        icon: <FileDoneOutlined />,
        redirect: null,
        children: [
          ...(tenantId === 'queimadiaria'
            ? [
                {
                  label: 'Gerenciador',
                  key: '15.1',
                  icon: null,
                  redirect: '/link-generator',
                },
                {
                  label: 'Encurtador',
                  key: '15.2',
                  icon: null,
                  redirect: '/link-shortner',
                },
                {
                  label: 'Modal redirect',
                  key: '15.3',
                  icon: null,
                  redirect: '/modal-redirect',
                },
              ]
            : [
                {
                  label: 'Encurtador',
                  key: '15.2',
                  icon: null,
                  redirect: '/link-shortner',
                },
                {
                  label: 'Modal redirect',
                  key: '15.3',
                  icon: null,
                  redirect: '/modal-redirect',
                },
              ]),
        ],
      },
      {
        label: 'Loja',
        key: '17',
        icon: <ShoppingCartOutlined />,
        redirect: null,
        children: [
          {
            label: 'Cadastro de Itens',
            key: '17.1',
            icon: null,
            redirect: '/marketplace',
          },
          {
            label: 'Banners',
            key: '17.2',
            icon: null,
            redirect: null,
            children: [
              {
                label: 'Gerenciamento',
                key: '17.2.1',
                icon: null,
                redirect: '/marketplace/banners/gerenciamento',
              },
              {
                label: 'Ordenação',
                key: '17.2.2',
                icon: null,
                redirect: '/marketplace/banners/ordenacao',
              },
            ],
          },
        ],
      },
    ].filter(item => item !== null);
  }, [hasPermissionMaster, tenantId]);

  const buildChildren = (children?: BaseMenuItem[]): MenuItem[] | undefined => {
    if (!children?.length) return undefined;

    return children.map(child =>
      setItem(
        child.label,
        child.key,
        child.icon,
        child.redirect,
        buildChildren(child.children)
      )
    );
  };

  const items = useMemo<MenuItem[]>(() => {
    const baseItems: MenuItem[] = [];

    baseMenuItems.forEach(item => {
      baseItems.push(
        setItem(
          item.label,
          item.key,
          item.icon,
          item.redirect,
          buildChildren(item.children)
        )
      );
    });

    if (hasPermissionMaster) {
      baseItems.push(
        setItem('Gestão de tenants', '16', <BranchesOutlined />, '/tenants')
      );
    }

    return baseItems;
  }, [baseMenuItems, buildChildren, hasPermissionMaster]);

  const { selectedKeys, openKeys } = useMemo(() => {
    const currentPath = location.pathname;

    const isExactOrDeepMatch = (
      path: string,
      redirect: string | null
    ): boolean => {
      if (!redirect) return false;
      if (path === redirect) return true;
      // Only match as prefix if followed by '/' to avoid '/marketplace' matching '/marketplace/banners'
      return path.startsWith(`${redirect}/`);
    };

    // Returns { leafKey, parentKeys } by going deepest-first (children before parent)
    const findMatch = (
      items: BaseMenuItem[],
      path: string,
      parents: string[] = []
    ): { leafKey: string; parentKeys: string[] } | null => {
      for (const item of items) {
        if (item.children) {
          const result = findMatch(item.children, path, [...parents, item.key]);
          if (result) return result;
        }
      }

      // No child matched — find best leaf at this level (longest redirect = most specific)
      let best: BaseMenuItem | null = null;
      for (const item of items) {
        if (!item.children && isExactOrDeepMatch(path, item.redirect)) {
          const len = item.redirect?.length ?? 0;
          const bestLen = best?.redirect?.length ?? 0;
          if (len > bestLen) best = item;
        }
      }

      return best ? { leafKey: best.key, parentKeys: parents } : null;
    };

    const result = findMatch(baseMenuItems, currentPath);

    return {
      selectedKeys: result ? [result.leafKey] : [],
      openKeys: result ? result.parentKeys : [],
    };
  }, [location.pathname, baseMenuItems]);

  return { items, selectedKeys, openKeys };
}
